import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type {
  ContactListChannel,
  ContactListConsent,
  OnboardingContactRow,
} from "@care-by-ella/contracts";
import { useAuth } from "@/auth/AuthContext";
import { UnauthorizedError } from "@/lib/clinician-api";
import {
  CHANNEL_OPTIONS,
  CONSENT_OPTIONS,
  CONTACT_CHANNEL_LABEL,
  CONTACT_CONSENT_LABEL,
  contactViewRow,
} from "@/lib/contact-list";
import { fetchContactList } from "@/lib/contact-list-api";
import {
  rangeEndingAt,
  rangeError,
  stockholmToday,
} from "@/lib/onboarding-funnel";
import { adminPatientHref } from "@/lib/patient-links";

// TES-1109 (Emelie 2026-10-05) — Kontaktlista: the people with a verified
// e-mail address whose start falls in the period, how far they got in
// onboarding, and whether they may be contacted for product feedback.
//
// Admin only, like Bokningsdiagnostik: the list names people, so the API
// logs it as a read of each patient it returns. The filters are sent to the
// API so only the people shown are read. "Patienten" opens her through the
// admin's support view, which asks for today's support reason there.

const PRESETS = [
  { days: 7, label: "7 dagar" },
  { days: 30, label: "30 dagar" },
  { days: 90, label: "90 dagar" },
] as const;

const SELECT_CLASS =
  "px-sm py-xs rounded-md border border-surfaceMuted bg-surface text-sm text-textPrimary";

export function OnboardingContactsRoute() {
  const { signOut, claims } = useAuth();
  const signOutRef = useRef(signOut);
  signOutRef.current = signOut;
  const [range, setRange] = useState(() => rangeEndingAt(stockholmToday(), 30));
  const [channel, setChannel] = useState<ContactListChannel | null>(null);
  const [consent, setConsent] = useState<ContactListConsent | null>(null);
  const [rows, setRows] = useState<OnboardingContactRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const invalid = rangeError(range.from, range.to);

  const load = useCallback(async () => {
    if (rangeError(range.from, range.to)) return;
    setError(null);
    try {
      const list = await fetchContactList(range.from, range.to, {
        channel,
        consent,
      });
      setRows(list.rows);
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        void signOutRef.current();
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [range, channel, consent]);

  useEffect(() => {
    setRows(null);
    void load();
  }, [load]);

  return (
    <div className="flex flex-col gap-md">
      <div className="flex items-start justify-between gap-md flex-wrap">
        <div className="flex flex-col gap-xs">
          <h1 className="font-display text-3xl tracking-tight">Kontaktlista</h1>
          <p className="text-sm text-textSecondary max-w-2xl">
            Personer med verifierad e-post som började under perioden. Internt:
            listan loggas som åtkomst till varje person den visar. En
            e-postadress är aldrig ett samtycke – bara ”Samtycke” får kontaktas
            för produktfeedback.
          </p>
        </div>
        <div className="flex flex-col gap-sm items-end">
          <div className="flex gap-xs" role="group" aria-label="Snabbval">
            {PRESETS.map((p) => (
              <button
                key={p.days}
                type="button"
                onClick={() =>
                  setRange(rangeEndingAt(stockholmToday(), p.days))
                }
                className="px-md py-sm rounded-md border border-surfaceMuted text-sm"
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="flex gap-sm items-end flex-wrap justify-end">
            <label className="flex flex-col gap-xs text-xs text-textSecondary">
              Från
              <input
                type="date"
                value={range.from}
                max={range.to}
                onChange={(e) =>
                  setRange((r) => ({ ...r, from: e.target.value }))
                }
                className={SELECT_CLASS}
              />
            </label>
            <label className="flex flex-col gap-xs text-xs text-textSecondary">
              Till
              <input
                type="date"
                value={range.to}
                min={range.from}
                onChange={(e) =>
                  setRange((r) => ({ ...r, to: e.target.value }))
                }
                className={SELECT_CLASS}
              />
            </label>
            <label className="flex flex-col gap-xs text-xs text-textSecondary">
              Kanal
              <select
                value={channel ?? ""}
                onChange={(e) =>
                  setChannel((e.target.value || null) as typeof channel)
                }
                className={SELECT_CLASS}
              >
                <option value="">Alla kanaler</option>
                {CHANNEL_OPTIONS.map((c) => (
                  <option key={c} value={c}>
                    {CONTACT_CHANNEL_LABEL[c]}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-xs text-xs text-textSecondary">
              Samtycke produktfeedback
              <select
                value={consent ?? ""}
                onChange={(e) =>
                  setConsent((e.target.value || null) as typeof consent)
                }
                className={SELECT_CLASS}
              >
                <option value="">Alla</option>
                {CONSENT_OPTIONS.map((c) => (
                  <option key={c} value={c}>
                    {CONTACT_CONSENT_LABEL[c]}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {invalid ? (
            <p className="text-xs text-danger" role="alert">
              {invalid}
            </p>
          ) : null}
        </div>
      </div>

      {error ? (
        <div className="bg-danger/10 border border-danger/30 text-danger rounded-md p-md text-sm">
          {error}
        </div>
      ) : null}

      {rows === null && !error ? (
        <p className="text-sm text-textSecondary">Hämtar…</p>
      ) : null}

      {rows ? <ContactListView rows={rows} actingRole={claims?.role} /> : null}
    </div>
  );
}

/** The table itself, or the empty state. Exported for the tests. */
export function ContactListView({
  rows,
  actingRole,
}: {
  rows: OnboardingContactRow[];
  actingRole: string | undefined;
}) {
  if (rows.length === 0) {
    return (
      <div className="bg-surface border border-surfaceMuted rounded-md p-md text-textSecondary text-sm">
        Ingen med verifierad e-post matchar perioden och filtren.
      </div>
    );
  }
  return (
    <div className="border border-surfaceMuted rounded-md bg-surface overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs uppercase tracking-wide text-textSecondary text-left">
            <th className="px-md py-sm font-medium">Namn</th>
            <th className="px-md py-sm font-medium">E-post</th>
            <th className="px-md py-sm font-medium">Verifierad</th>
            <th className="px-md py-sm font-medium">Senaste steg</th>
            <th className="px-md py-sm font-medium">Senast aktiv</th>
            <th className="px-md py-sm font-medium">Kanal</th>
            <th className="px-md py-sm font-medium">
              Samtycke produktfeedback
            </th>
            <th className="px-md py-sm font-medium" />
          </tr>
        </thead>
        <tbody>
          {rows.map(contactViewRow).map((r) => (
            <tr
              key={r.patientId}
              className="border-t border-surfaceMuted align-top"
              data-consent={r.mayContact ? "granted" : "not-granted"}
            >
              <td className="px-md py-sm font-medium">{r.name}</td>
              <td className="px-md py-sm break-all">{r.email}</td>
              <td className="px-md py-sm">{r.verified}</td>
              <td className="px-md py-sm">
                {r.lastStep}
                <span className="block text-xs text-textSecondary">
                  {r.lastStepDay}
                </span>
              </td>
              <td className="px-md py-sm">{r.lastActivityDay}</td>
              <td className="px-md py-sm">{r.channel}</td>
              <td className="px-md py-sm">
                <span
                  className={
                    r.mayContact ? "text-textPrimary" : "text-textSecondary"
                  }
                >
                  {r.consent}
                </span>
                {r.consentDay ? (
                  <span className="block text-xs text-textSecondary">
                    {r.consentDay}
                  </span>
                ) : null}
              </td>
              <td className="px-md py-sm">
                <Link
                  to={adminPatientHref(actingRole, r.patientId)}
                  className="text-brand hover:underline"
                >
                  Patienten
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
